/**
 * The world — TRD-01 §6.
 *
 * The invariant everything else depends on: `step()` is a pure function of
 * (current state, commands). No wall-clock, no I/O, no unseeded randomness.
 *
 * SCOPE: TRD-01 delivers the chassis — seeded stream, entity pools, the fixed 13-system
 * pipeline, the divergence hash, and headless replay. The systems themselves are stubs
 * that land with TRD-05 (combat), TRD-06 (waves), TRD-07 (economy), TRD-08 (synergy).
 * Each stub names its owner.
 */
import {
  TICK_RATE,
  SIM_VERSION,
  SUPPORT_SURGE_COOLDOWN_WAVES,
  TOWER_DISABLE_RECOVERY_TICKS,
} from './constants.js';
import type { Command } from './commands.js';
import { createEconomyState, type EconomyState } from './economy.js';
import type { SynergyEffect } from './effects.js';
import type { SimEvent } from './events.js';
import {
  createDronePool,
  createEnemyPool,
  createProjectilePool,
  createTowerPool,
  createZonePool,
  type Drone,
  type Enemy,
  type EntityPool,
  type Projectile,
  type Tower,
  type Zone,
} from './entities.js';
import { FNV_OFFSET_BASIS, Hasher } from './hash.js';
import { createRunMods, type RunMods } from './mods.js';
import { Rng } from './rng.js';
import { SYSTEM_ORDER, type SystemName } from './systems/order.js';
import { applyDamage } from './systems/damage.js';
import { canSummon, stepDrones } from './systems/drones.js';
import { stepZones } from './systems/zones.js';
import { MAX_LIVE_ENEMIES, resolveDeaths, tickMenders } from './systems/enemies.js';
import { compilePaths, pathForGroup, resolvePosition, type CompiledPath } from './systems/paths.js';
import {
  advanceFireSchedule,
  cooldownTicks,
  fireTower,
  stepProjectiles,
  type FireContext,
} from './systems/projectiles.js';
import { applyOnKillHooks, type HookContext } from './systems/hooks.js';
import { SpatialGrid } from './systems/spatial.js';
import {
  DEFAULT_STATUS_TUNING,
  applyStatus,
  effectiveSpeed,
  tickStatuses,
  type StatusTuning,
} from './systems/status.js';
import {
  activeTierFor,
  countClasses,
  emptyRuntime,
  recomputeSynergies,
  type SynergyRuntime,
} from './systems/synergy.js';
import { acquireTarget, effectiveRange, isTargetingMode } from './systems/targeting.js';
import {
  buildSpawnSchedule,
  hpJitterFrom,
  ROLL_LANE,
  type ScheduledSpawn,
} from './systems/waves.js';
import {
  DEFAULT_BALANCE,
  hasFlag,
  type BalanceSpec,
  type BuffTileSpec,
  type EnemyArchetype,
  type EnemyTable,
  type PathSpec,
  type Point,
  type ClassTable,
  type RunEffect,
  type TowerSpec,
  type TowerTable,
  type WaveSpec,
} from './scenario.js';
import {
  applyTowerSpec,
  canPlace,
  computePayout,
  expireDronesOf,
  firstEmptyBenchSlot,
  rerollCost,
  mergeReadyFor,
  mergeTower,
  resolveMerges,
  rollShop,
  sellRefund,
  type MergeContext,
} from './systems/economy.js';
import { angleTo, cos, distSq, sin } from './math.js';
import { DT } from './constants.js';
import {
  CLASS_IDS,
  NO_TARGET,
  type ClassId,
  type EntityId,
  type GoldReason,
  type MapId,
  type Phase,
  type StarLevel,
  type StatusKind,
  type TargetingMode,
  type TowerId,
} from './types.js';
import type { SynergyDelta, SynergyStatus } from './effects.js';

/** R-06-05 — a flyer leaks once it is within half a tile of the Core. */
const LEAK_RADIUS_SQ = 0.25;

export interface RunConfig {
  mapId: MapId;
  isDaily: boolean;
  /** Rejected by the server if it disagrees with its own (TRD-10 R-10-17). */
  simVersion: number;

  coreMaxHp: number;
  startingGold: number;
  benchSlots: number;
  shopSlots: number;
  placementsPerRound: number;
  /** 25 on authored maps, 22–28 on generated dailies (TRD-13 R-13-19). Never hardcode. */
  totalWaves: number;

  /**
   * The authored content this run plays.
   *
   * Handed in as data rather than looked up, so the wave director cannot tell an authored
   * wave from a generated daily one (R-06-08b) and the replay server can rebuild a run
   * from storage without the content package.
   */
  paths: readonly PathSpec[];
  core: Point;
  waves: readonly WaveSpec[];
  enemyTable: EnemyTable;
  /** One string per map row; `.` marks a buildable tile (TRD-02 R-02-12). */
  buildMask: readonly string[];
  /** Tiles that change the tower standing on them (see `BuffTileSpec`). Empty = none. */
  buffTiles: readonly BuffTileSpec[];
  /** The towers this run may roll, already filtered to the unlocked pool. */
  towerPool: readonly TowerSpec[];
  /** Class definitions and their 2/4/6 tiers — the synergy web (TRD-08 Part A). */
  classTable: ClassTable;
  /** Mutator + meta-tree effects, applied at recompute (R-08-22b). Empty = none. */
  runEffects: readonly RunEffect[];
  balance: BalanceSpec;
}

export const DEFAULT_RUN_CONFIG: Omit<RunConfig, 'mapId' | 'isDaily'> = {
  simVersion: SIM_VERSION,
  coreMaxHp: 20,
  startingGold: 12,
  benchSlots: 6,
  shopSlots: 4,
  placementsPerRound: 2,
  totalWaves: 25,
  // Empty by default so a bare `makeRunConfig()` still constructs a valid, inert world —
  // which is what the determinism suite steps when it is measuring the chassis itself.
  paths: [],
  core: { x: 0, y: 0 },
  waves: [],
  enemyTable: {},
  buildMask: [],
  buffTiles: [],
  towerPool: [],
  classTable: {},
  runEffects: [],
  balance: DEFAULT_BALANCE,
};

export function makeRunConfig(overrides: Partial<RunConfig> = {}): RunConfig {
  return {
    mapId: 'the-furrow',
    isDaily: false,
    ...DEFAULT_RUN_CONFIG,
    ...overrides,
  };
}

export interface ReplayLog {
  commands: readonly { tick: number; cmd: Command }[];
  finalTick: number;
  /** Per-run shop seed. Omitted replays the legacy shop-follows-the-day behaviour. */
  shopSeed?: bigint;
}

export interface WorldOptions {
  /**
   * Seeds the shop. The client rolls a fresh one per run and sends it with the
   * submission; the server replays with the same value (TRD-11 §5).
   */
  shopSeed?: bigint;
}

export interface RunResult {
  score: number;
  wavesCleared: number;
  coreHp: number;
  victory: boolean;
  /** World.runHash — the rolling digest, not hash() (TRD-00 R-00-08). */
  hash: number;
  finalTick: number;
}

export interface UiSnapshot {
  tick: number;
  /**
   * The shielded boss on the board, for the boss bar (playtest request). Null whenever
   * there isn't one, which is every wave but the finale.
   *
   * Keyed off "has a shield phase" rather than off Prism's id, because the sim should not
   * know which archetype is the finale — an authored `shieldHp` IS the thing worth
   * drawing two bars for, and today Prism is the only enemy that has one.
   *
   * `downUntilTick` is the sim's own deadline rather than a duration the UI counts down
   * itself: a renderer running its own clock drifts from the simulation the server
   * replays, and this window is short enough for that to show.
   */
  boss: {
    archetype: string;
    hp: number;
    maxHp: number;
    shieldHp: number;
    maxShieldHp: number;
    /** 0 while the shield is up. Otherwise the tick the shield comes back. */
    downUntilTick: number;
    /** Still rising out of the ground — untouchable, so the bars should say so. */
    emerging: boolean;
  } | null;
  phase: Phase;
  wave: number;
  totalWaves: number;
  coreHp: number;
  coreMaxHp: number;
  gold: number;
  placementsLeft: number;
  rerollCost: number;
  /**
   * Shop slots enriched for the cards (TRD-09 §6). `wouldMerge` and `deltas` are
   * computed HERE, not in React: merge needs `MERGE_COUNT` and the preview needs the
   * synergy engine, and the UI reimplementing either is exactly the drift TRD-09 §2
   * forbids. Recomputed per publish (~20 Hz), which is what makes the card update the
   * moment a placement changes the class counts under it.
   */
  shop: ({
    defId: string;
    cost: number;
    /** Buying this makes a third copy and auto-merges to ★2 (TRD-09 R-09-09). */
    wouldMerge: boolean;
    /** The merge completes on the BENCH alone — legal even with a full bench (R-07-10a). */
    mergesOnBench: boolean;
    /**
     * Copies of this tower the player already holds (playtest request: "in the shop see
     * what towers you already have"). `ownedStarOne` is the count that could still merge
     * with this offer — ★1s on the bench and on the board — and `owned` is every copy at
     * any star, so a card whose ★2 is already built still reads as owned rather than new.
     *
     * Computed here rather than in React for the usual reason: `MERGE_COUNT` is balance
     * data, and a UI that counted copies itself would be a second implementation of the
     * merge rule sitting one refactor away from disagreeing with the real one.
     */
    ownedStarOne: number;
    owned: number;
    /** One entry per class on the tower — the "+1 Frost → tier 4" preview (R-08-15). */
    deltas: SynergyDelta[];
  } | null)[];
  /** Bench items with the sim-computed refund — the UI must not know SELL_REFUND_RATE —
   *  and the same placement deltas the shop cards get, for the bench inspector (B-11). */
  bench: ({
    defId: TowerId;
    star: StarLevel;
    investedGold: number;
    sellRefund: number;
    deltas: SynergyDelta[];
  } | null)[];
  /** R-07-26 — drives the padlock button; a locked shop survives wave clears. */
  shopLocked: boolean;
  /**
   * The upcoming wave's composition, shown during the build phase (backlog B-4) —
   * groups merged per enemy, in first-appearance order. Null once the wave starts.
   */
  nextWave: { defId: string; count: number; boss: boolean }[] | null;
  /** True while a boss wave is on the field / when the NEXT wave is one (backlog B-3). */
  currentWaveIsBoss: boolean;
  nextWaveIsBoss: boolean;
  placedTowers: {
    id: EntityId;
    defId: string;
    star: 1 | 2 | 3;
    tileX: number;
    tileY: number;
    /**
     * EFFECTIVE range in tiles, after synergy mods — what the range circle must show.
     * A circle drawn from base range lies to the player the moment Marksman-2 lands.
     */
    range: number;
    /** Effective damage per hit before crit/armor: (base × star + flat) × multiplier. */
    damage: number;
    /** Base × star scaling, before any synergy — the anchor the breakdown explains. */
    baseDamage: number;
    /** Effective seconds between shots, after attack-speed mods. */
    attackInterval: number;
    critChance: number;
    targetingMode: TargetingMode;
    /** Lifetime damage attributed to this tower — the run summary's bar chart (R-09-26). */
    damageDealt: number;
    /** Damage dealt by towers this one was buffing (B-12 stretch) — the aura carrier's stat. */
    assistDealt: number;
    /** Exact gold returned on sell (TRD-09 R-09-20 — "sell button with the exact refund"). */
    sellRefund: number;
    /** Enough copies exist for a manual merge — drives the shop/inspector badge. */
    mergeReady: boolean;
    /**
     * The support surge (playtest request), in the four states the hotbar draws.
     *
     * `support` marks a tower that has one at all. `surgeActive` is live right now;
     * `surgeArmed` is spent on the round about to start and waiting for it; `surgeReady`
     * means pressing it would do something; `surgeBackInWaves` counts the rounds until
     * that becomes true again, and is 0 whenever it already is.
     *
     * All decided here rather than in React: "can I press this" is a rule, and a UI that
     * worked it out independently would eventually offer a button the sim refuses.
     */
    support: boolean;
    surgeActive: boolean;
    surgeArmed: boolean;
    surgeReady: boolean;
    surgeBackInWaves: number;
  }[];
  synergies: SynergyStatus[];
  enemiesRemaining: number;
  /** Board cap (TRD-07 R-07-27) — the UI has to show why a placement was refused. */
  towersPlaced: number;
  maxTowers: number;
}

/** Gameplay draws from stream 1; the daily generator uses stream 2 (TRD-13 R-13-04). */
const GAMEPLAY_STREAM = 1n;
/**
 * The shop draws from stream 3, off a seed of its OWN (playtest request: "the shop
 * should always just be random instead of going off the seed").
 *
 * Two things fall out of the split, and both were wanted:
 *
 *  - The offers are no longer a property of the day. Two players on the same daily get
 *    different shops, and restarting re-rolls yours, which is what makes the shop feel
 *    like a draw rather than a script.
 *  - Shop rolls stop shifting the gameplay stream. Rerolling used to move every wave's
 *    HP jitter downstream of it; now the two are genuinely independent.
 *
 * It is still a SEED, not `Math.random`: the run stays perfectly replayable, so the
 * server can re-simulate a submission exactly. `shopSeed` simply travels with the run
 * instead of being derived from the date.
 */
const SHOP_STREAM = 3n;

/**
 * Monoculture (TRD-13 §6) — the shop offers only `classCount` classes for the whole run.
 *
 * Drawn once from the SHOP stream, before the opening roll: the restriction is a property
 * of the RUN, not of a round. Re-drawing it per roll would let a reroll shop for a
 * different class list, which is the opposite of what the mutator promises.
 *
 * Classes with nothing in the pool are excluded before the draw, and a restriction that
 * would empty the pool is dropped outright — an empty shop is not a harder day, it is a
 * broken one, and `rollSlot` would hand back four dead slots the player paid to reroll.
 */
function restrictPool(
  pool: readonly TowerSpec[],
  classCount: number,
  rng: Rng,
): readonly TowerSpec[] {
  const present = CLASS_IDS.filter((id) => pool.some((spec) => spec.classes.includes(id)));
  if (present.length <= classCount) return pool;

  const bag = present.slice();
  rng.shuffle(bag);
  const allowed = new Set<ClassId>(bag.slice(0, classCount));
  const restricted = pool.filter((spec) => spec.classes.some((id) => allowed.has(id)));
  return restricted.length > 0 ? restricted : pool;
}

/**
 * How often full entity state is folded into the rolling run hash.
 *
 * Every tick contributes a cheap digest. Folding full state every tick would be
 * O(entities) × 90 000 ticks, which threatens the server's 2-second replay budget
 * (R-01-15) once 400 enemies are on the field. Once per second of sim time pins down any
 * real divergence at 1/60th the cost.
 */
const FULL_HASH_INTERVAL = 60;

export class World {
  readonly seed: bigint;
  readonly config: RunConfig;
  readonly rng: Rng;
  /** Per-RUN, not per-day: the shop is its own draw (see SHOP_STREAM). */
  readonly shopSeed: bigint;
  readonly shopRng: Rng;

  tick = 0;
  phase: Phase = 'build';
  wave = 0;

  coreHp: number;
  readonly coreMaxHp: number;

  readonly enemies: EntityPool<Enemy> = createEnemyPool();
  readonly towers: EntityPool<Tower> = createTowerPool();
  readonly projectiles: EntityPool<Projectile> = createProjectilePool();
  readonly drones: EntityPool<Drone> = createDronePool();
  readonly zones: EntityPool<Zone> = createZonePool();

  readonly economy: EconomyState;
  readonly runMods: RunMods = createRunMods();

  /** Drained and cleared by the client each frame (TRD-00 §7). */
  events: SimEvent[] = [];

  /** Includes commands the sim rejected as no-ops (TRD-07 R-07-08). */
  commandsApplied = 0;

  /** Set by place/sell/move/merge; consumed by synergyRecompute (TRD-08 R-08-04). */
  synergyDirty = false;

  victory = false;

  /** Rebuilt by synergyRecompute; read by the per-tick field effects and the UI. */
  private synergy: SynergyRuntime = emptyRuntime();
  /** High-water mark of `bonusCoreHp` already paid out as hearts — see recompute. */
  private bonusCoreHpGranted = 0;
  /** Monoculture mutator shifts every threshold (TRD-13 §6). */
  thresholdShift = 0;

  private nextEntityId = 1;
  private rolling = FNV_OFFSET_BASIS;
  private currentCommands: readonly Command[] = EMPTY_COMMANDS;
  private readonly pipeline: readonly (() => void)[];

  /** Rebuilt each tick in `towerTargeting` (TRD-05 R-05-01). */
  private readonly grid = new SpatialGrid();
  /** One reusable buffer for every range query in the tick — see SpatialGrid.query. */
  private readonly scratchIds: EntityId[] = [];
  private readonly allocateIdBound = (): EntityId => this.allocateId();
  private readonly awardGoldBound = (amount: number): void => this.earnGold(amount, 'drop');

  /** Compiled once at construction (TRD-06 R-06-01), never per enemy or per tick. */
  private readonly paths: readonly CompiledPath[];
  private readonly core: Point;
  /**
   * Buff tiles by position (playtest request). Keyed on the tile rather than written onto
   * the tower, because the buff belongs to the GROUND — move the tower off and it has to
   * stop applying, and a lookup by position is the only version of that which cannot go
   * stale behind a `move` command.
   */
  private readonly buffTiles = new Map<number, BuffTileSpec>();
  /**
   * Terrain opacity, as a bound arrow so the hot targeting loop allocates nothing.
   *
   * Out of bounds counts as CLEAR rather than solid: a shot along the map edge is not
   * blocked by the void beyond it, and treating the outside as opaque would silently
   * blind towers near the border.
   */
  private readonly blocksSight = (tileX: number, tileY: number): boolean =>
    this.config.buildMask[tileY]?.[tileX] === '#';
  /** Waves cleared while a Core-HP tile was occupied, for its every-Nth-wave payout. */
  private coreTileWaves = 0;
  private readonly enemyTable: EnemyTable;
  private readonly towerPool: readonly TowerSpec[];
  /** What the SHOP may offer. Identical to `towerPool` unless Monoculture narrows it —
   *  `towerTable` keeps every spec regardless, so a tower already on the board is still
   *  resolvable after the restriction lands. */
  private readonly shopPool: readonly TowerSpec[];
  private readonly towerTable: TowerTable;
  private readonly balance: BalanceSpec;

  /** Built in full at wave start and consumed by a cursor (R-06-08). */
  private schedule: readonly ScheduledSpawn[] = [];
  private spawnCursor = 0;
  /** Ticks since this wave started; the schedule is expressed against it, not `tick`. */
  private waveTick = 0;

  private readonly spawnChildBound = (
    archetype: EnemyArchetype,
    parent: Enemy,
    pathT: number,
    depth: number,
  ): void => this.spawnChild(archetype, parent, pathT, depth);

  private readonly awardBountyBound = (amount: number): void => {
    if (amount <= 0) return;
    this.economy.gold += amount;
    this.economy.goldEarnedTotal += amount;
    this.events.push({ t: 'goldChanged', delta: amount, total: this.economy.gold, reason: 'drop' });
  };

  /**
   * @param options.shopSeed Seeds the shop stream. Omitted, it falls back to the run
   *   seed — which keeps hand-built worlds and every existing test deterministic without
   *   them having to know this exists.
   */
  constructor(seed: bigint, config: RunConfig, options: WorldOptions = {}) {
    this.seed = seed;
    this.config = config;
    this.rng = new Rng(seed, GAMEPLAY_STREAM);
    this.shopSeed = options.shopSeed ?? seed;
    this.shopRng = new Rng(this.shopSeed, SHOP_STREAM);

    this.coreMaxHp = config.coreMaxHp;
    this.coreHp = config.coreMaxHp;
    this.paths = compilePaths(config.paths);
    // Tile CENTER, matching the compiled paths' half-tile shift — the leak radius is
    // measured against this, so the Core must live where the path actually ends.
    this.core = { x: config.core.x + 0.5, y: config.core.y + 0.5 };
    for (const tile of config.buffTiles) this.buffTiles.set(tile.y * 1000 + tile.x, tile);
    this.enemyTable = config.enemyTable;
    this.towerPool = config.towerPool;
    this.balance = config.balance;
    const towerTable: Record<string, TowerSpec> = {};
    for (const spec of config.towerPool) towerTable[spec.id] = spec;
    this.towerTable = towerTable;
    this.economy = createEconomyState(
      // Resolved HERE rather than in the synergy recompute, and this is the whole reason
      // the grant is its own effect kind: the purse has to be full before the first build
      // phase, and no mod exists yet at construction. A player who cannot see the gold
      // before their first purchase does not have it.
      config.startingGold + startingGoldBonus(config.runEffects),
      config.benchSlots,
      config.shopSlots,
      config.placementsPerRound,
    );

    // Threshold shifts are config-static — a property of the run's rules, not of the
    // board — so they resolve once here rather than on every recompute. `applyEffect`
    // deliberately does not handle the kind: a shift must be in force BEFORE the tier
    // pass that reads it, and constructor-time is unambiguously before.
    let shopClassCount = 0;
    for (const runEffect of config.runEffects) {
      for (const effect of runEffect.effects) {
        if (effect.k === 'thresholdShift') this.thresholdShift += effect.delta;
        // Monoculture narrows the shop for the whole run. Config-static for the same
        // reason the shift is, and it must settle before the opening roll below.
        if (effect.k === 'shopClassRestriction') {
          shopClassCount =
            shopClassCount === 0 ? effect.classCount : Math.min(shopClassCount, effect.classCount);
        }
      }
    }
    this.shopPool =
      shopClassCount > 0
        ? restrictPool(this.towerPool, shopClassCount, this.shopRng)
        : this.towerPool;

    // Built once from SYSTEM_ORDER so the declared order and the executed order cannot
    // drift apart. The Record type makes omitting a system a compile error.
    const handlers: Record<SystemName, () => void> = {
      applyCommands: () => this.sysApplyCommands(),
      synergyRecompute: () => this.sysSynergyRecompute(),
      waveDirector: () => this.sysWaveDirector(),
      statusTick: () => this.sysStatusTick(),
      enemyMovement: () => this.sysEnemyMovement(),
      towerTargeting: () => this.sysTowerTargeting(),
      enemyAbilities: () => this.sysEnemyAbilities(),
      towerFiring: () => this.sysTowerFiring(),
      projectileMovement: () => this.sysProjectileMovement(),
      summonTick: () => this.sysSummonTick(),
      deathResolution: () => this.sysDeathResolution(),
      leakResolution: () => this.sysLeakResolution(),
      waveCompletion: () => this.sysWaveCompletion(),
      compaction: () => this.sysCompaction(),
    };
    this.pipeline = SYSTEM_ORDER.map((name) => handlers[name]);

    // TRD-07 R-07-01 — the shop rolls automatically and for free at the start of EVERY
    // build phase, and a run opens in one. Without this the player starts wave 1 staring
    // at four empty slots and has to pay 2 gold to see their first offers.
    //
    // Rolled here rather than on the first tick so it lands before the opening UI
    // snapshot, and so the draw stays at a fixed point in the RNG stream (R-07-04).
    rollShop(this.economy, this.shopPool, this.balance, 1, this.shopRng);

    // Apply `config.runEffects` NOW, not on the first placement: a mutator's Core
    // reduction or a meta node's start bonus must be true at tick 0 — a Glass Cannon
    // day showed 20 Lives until a tower dirtied the synergy state (B-1). Constructor
    // rather than first-tick, so tests that inject mods post-construction keep them.
    this.synergyDirty = true;
    this.sysSynergyRecompute();
  }

  /** Monotonic, never recycled within a run (TRD-01 R-01-09). */
  allocateId(): EntityId {
    return this.nextEntityId++;
  }

  /** Advance exactly one tick of DT seconds. */
  step(commands: readonly Command[]): void {
    this.events.length = 0;

    // An ended run is over, and stepping it further is not.
    //
    // Nothing changes the score once `endRun` has fired, but the tick counter and the
    // rolling hash kept moving, and the client's loop steps several times per frame and
    // only notices the end when it drains events — so it submitted a hash folded over
    // ticks the server would never replay. `replayDetailed` stops the moment the phase
    // is `ended`, so the two could not agree, and the run was refused as a mismatch
    // through no fault of the player's.
    //
    // Returning here also makes the submitted `finalTick` mean what it says: the tick the
    // run actually ended on.
    if (this.phase === 'ended') return;

    this.currentCommands = commands;

    for (let i = 0; i < this.pipeline.length; i++) {
      (this.pipeline[i] as () => void)();
    }

    this.currentCommands = EMPTY_COMMANDS;
    this.tick++;
    this.foldTickIntoRunHash();
  }

  // ── Systems ─────────────────────────────────────────────────────────────────────────
  // Order is fixed by SYSTEM_ORDER. See systems/order.ts before touching any of this.

  /** 1. TRD-07 owns buy/reroll/place/move/sell; TRD-05 owns setTargeting. */
  private sysApplyCommands(): void {
    for (const cmd of this.currentCommands) {
      this.commandsApplied++;

      switch (cmd.t) {
        case 'startWave':
          if (this.phase === 'build') {
            this.phase = 'combat';
            this.wave++;
            this.beginWave();
            // R-05-12 — every tower opens the wave ready to fire, so wave 1 starts on a
            // synchronised volley instead of towers trickling in on whatever cooldown
            // phase they happened to be left on.
            this.resetTowerCooldowns();
            // An armed surge goes live with the wave, and a spent one goes out with it.
            // Both are mod changes that no board edit announces, so the two phase
            // transitions raise the flag themselves.
            this.synergyDirty = true;
            this.events.push({ t: 'waveStarted', wave: this.wave });
          }
          break;

        // The support surge (playtest request) — the only command that is legal DURING
        // combat, because it is the only thing the player is meant to do mid-wave.
        case 'surge': {
          // Legal in BUILD as well as combat (playtest request): a player who can read the
          // next wave should be able to spend it before the round rather than scrambling
          // for the key once the round is already going wrong.
          if (this.phase === 'ended') break;
          const tower = this.towers.get(cmd.tower);
          if (!tower || !tower.alive) break;
          if (!this.towerTable[tower.defId]?.support) break;
          const target = this.surgeTargetWave();
          // Refuse a double-arm as well as a cooldown: without the first test, arming
          // twice in one build phase would silently push the cooldown out by nothing and
          // read to the player as a wasted press.
          if (target < tower.surgeReadyWave || tower.surgeWave === target) break;
          tower.surgeWave = target;
          tower.surgeReadyWave = target + SUPPORT_SURGE_COOLDOWN_WAVES;
          this.synergyDirty = true;
          this.events.push({
            t: 'supportSurged',
            tower: tower.id,
            defId: tower.defId,
            x: tower.x,
            y: tower.y,
            wave: target,
            armed: this.phase === 'build',
          });
          break;
        }

        // R-05-08 — a Command rather than direct mutation, so a targeting change made
        // mid-wave is captured in the replay log like any other player intent.
        case 'setTargeting': {
          const tower = this.towers.get(cmd.tower);
          if (tower && isTargetingMode(cmd.mode)) {
            tower.targetingMode = cmd.mode;
            tower.targetId = NO_TARGET;
          }
          break;
        }

        // A rejected command still counts as applied, so client and server agree on the
        // command stream even before these are implemented (TRD-07 R-07-08).
        case 'buy':
          this.cmdBuy(cmd.slot);
          break;
        case 'reroll':
          this.cmdReroll();
          break;
        case 'lockShop':
          // Toggle. Rejecting a redundant toggle would mean the client tracking sim
          // state; a toggle is self-describing in the replay log.
          this.economy.shopLocked = !this.economy.shopLocked;
          break;
        case 'place':
          this.cmdPlace(cmd.benchIndex, cmd.tileX, cmd.tileY);
          break;
        case 'move':
          this.cmdMove(cmd.tower, cmd.tileX, cmd.tileY);
          break;
        case 'sell':
          this.cmdSell(cmd.tower);
          break;
        case 'sellBench':
          this.cmdSellBench(cmd.benchIndex);
          break;
        case 'merge':
          this.cmdMerge(cmd.tower);
          break;
      }
    }
  }

  // ── Economy commands (TRD-07) ───────────────────────────────────────────────────────
  // Every one of these is a silent no-op when its preconditions fail. The command still
  // counted as applied above, which is what keeps the client's log and the server's
  // replay in agreement about what was attempted (R-07-08).

  private cmdBuy(slot: number): void {
    const offer = this.economy.shop[slot];
    if (!offer) return;
    if (this.economy.gold < offer.cost) return;

    const benchSlot = firstEmptyBenchSlot(this.economy);
    if (benchSlot >= 0) {
      this.spendGold(offer.cost, 'purchase');
      this.economy.shop[slot] = null;
      this.economy.bench[benchSlot] = {
        defId: offer.defId,
        star: 1,
        investedGold: offer.cost,
      };
      this.runMerges();
      return;
    }

    // R-07-10a — a full bench still accepts a buy that COMPLETES a bench merge: the
    // bought copy plus MERGE_COUNT−1 benched copies collapse into one, so no slot is
    // actually needed. (R-07-10's hard block stands for every other purchase; merges
    // with PLACED copies stay manual — they change class counts.)
    const need = this.balance.MERGE_COUNT - 1;
    const matches = this.benchStarOneMatches(offer.defId);
    if (matches.length < need) return;

    const consumed = matches.slice(0, need);
    let invested = offer.cost;
    for (const index of consumed) invested += this.economy.bench[index]?.investedGold ?? 0;

    this.spendGold(offer.cost, 'purchase');
    this.economy.shop[slot] = null;
    for (const index of consumed.slice(1)) this.economy.bench[index] = null;
    this.economy.bench[consumed[0] as number] = { defId: offer.defId, star: 2, investedGold: invested };
    this.events.push({ t: 'towersMerged', result: -1, defId: offer.defId, star: 2, x: 0, y: 0 });
    // The fresh ★2 may itself complete a higher bench merge — cascade like any buy.
    this.runMerges();
  }

  /** Bench indices holding ★1 copies of `defId`, lowest slots first (deterministic). */
  private benchStarOneMatches(defId: string): number[] {
    const matches: number[] = [];
    for (let i = 0; i < this.economy.bench.length; i++) {
      const item = this.economy.bench[i];
      if (item && item.defId === defId && item.star === 1) matches.push(i);
    }
    return matches;
  }

  private cmdReroll(): void {
    const cost = rerollCost(this.balance, this.economy.rerollsThisRound, this.runMods);
    if (this.economy.gold < cost) return;
    this.spendGold(cost, 'reroll');
    this.economy.rerollsThisRound++;
    // A manual reroll IS a request for a fresh shop — it clears the lock (R-07-26).
    this.economy.shopLocked = false;
    rollShop(this.economy, this.shopPool, this.balance, this.wave, this.shopRng);
  }

  /** Live placed towers — the board cap is measured against this. */
  placedTowerCount(): number {
    let n = 0;
    for (const t of this.towers.items) if (t.alive) n++;
    return n;
  }

  /**
   * Manual merge (TRD-07 §7).
   *
   * Build phase only: three towers becoming one changes class counts, and that is not a
   * decision to be making while a wave is walking the path.
   */
  private cmdMerge(towerId: EntityId): void {
    if (this.phase !== 'build') return;
    if (mergeTower(this.mergeContext(), towerId)) this.synergyDirty = true;
  }

  private cmdPlace(benchIndex: number, tileX: number, tileY: number): void {
    if (this.phase !== 'build') return;
    if (this.economy.placementsLeft <= 0) return;
    // The board cap (TRD-07 R-07-27). Placements pace expansion within a round; this caps
    // the board absolutely, which is what forces a composition rather than an accumulation.
    if (this.placedTowerCount() >= this.balance.MAX_PLACED_TOWERS) return;

    const item = this.economy.bench[benchIndex];
    if (!item) return;
    if (!canPlace(this.config.buildMask, this.towers, tileX, tileY)) return;

    const spec = this.towerTable[item.defId];
    const tower = this.towers.spawn(this.allocateId());
    if (spec) applyTowerSpec(tower, spec, tileX, tileY);
    else {
      tower.defId = item.defId;
      tower.tileX = tileX;
      tower.tileY = tileY;
      tower.x = tileX + 0.5;
      tower.y = tileY + 0.5;
    }
    tower.star = item.star;
    tower.investedGold = item.investedGold;

    this.economy.bench[benchIndex] = null;
    this.economy.placementsLeft--;
    this.synergyDirty = true;
    this.events.push({
      t: 'towerPlaced',
      tower: tower.id,
      defId: tower.defId,
      tileX,
      tileY,
    });
    this.runMerges();
  }

  /** R-07-15 — free in gold, one placement in budget. Without it a misclick ruins a run. */
  private cmdMove(towerId: EntityId, tileX: number, tileY: number): void {
    if (this.phase !== 'build') return;
    if (this.economy.placementsLeft <= 0) return;

    const tower = this.towers.get(towerId);
    if (!tower) return;
    if (!canPlace(this.config.buildMask, this.towers, tileX, tileY)) return;

    tower.tileX = tileX;
    tower.tileY = tileY;
    tower.x = tileX + 0.5;
    tower.y = tileY + 0.5;
    tower.targetId = NO_TARGET;
    this.economy.placementsLeft--;
    this.synergyDirty = true;
  }

  private cmdSell(towerId: EntityId): void {
    if (this.phase !== 'build') return;
    const tower = this.towers.get(towerId);
    if (!tower) return;

    const refund = sellRefund(tower.investedGold, this.balance);
    this.towers.kill(tower);
    expireDronesOf({ drones: this.drones }, tower.id);
    this.synergyDirty = true;
    this.events.push({
      t: 'towerSold',
      tower: tower.id,
      tileX: tower.tileX,
      tileY: tower.tileY,
      refund,
    });
    this.earnGold(refund, 'sell');
  }

  private cmdSellBench(benchIndex: number): void {
    const item = this.economy.bench[benchIndex];
    if (!item) return;
    this.economy.bench[benchIndex] = null;
    this.earnGold(sellRefund(item.investedGold, this.balance), 'sell');
  }

  private mergeContext(): MergeContext {
    return {
      economy: this.economy,
      towers: this.towers,
      drones: this.drones,
      table: this.towerTable,
      balance: this.balance,
      events: this.events,
      allocateId: this.allocateIdBound,
    };
  }

  /** Bench-only auto-merge. Placed towers merge on the player's command (TRD-07 §7). */
  private runMerges(): void {
    if (resolveMerges(this.mergeContext())) this.synergyDirty = true;
  }

  /** Gold spent never reduces `goldEarnedTotal` — scoring counts income, not holdings. */
  private spendGold(amount: number, reason: GoldReason): void {
    this.economy.gold -= amount;
    this.events.push({
      t: 'goldChanged',
      delta: -amount,
      total: this.economy.gold,
      reason,
    });
  }

  private earnGold(amount: number, reason: GoldReason): void {
    if (amount <= 0) return;
    this.economy.gold += amount;
    this.economy.goldEarnedTotal += amount;
    this.events.push({ t: 'goldChanged', delta: amount, total: this.economy.gold, reason });
  }

  /**
   * Build this wave's spawn schedule.
   *
   * The HP jitter is ONE draw for the whole wave (R-06-09). Per-enemy jitter would burn
   * RNG state proportional to enemy count, so the stream position after wave 12 would
   * depend on how many splitters happened to split — which makes hunting a divergence
   * far harder than it needs to be, and makes a seeded run feel merely random.
   */
  private beginWave(): void {
    const spec = this.config.waves.find((w) => w.wave === this.wave);
    this.waveTick = 0;
    this.spawnCursor = 0;

    if (!spec) {
      this.schedule = EMPTY_SCHEDULE;
      return;
    }

    const jitter = hpJitterFrom(this.rng.nextFloat());
    this.schedule = buildSpawnSchedule(spec, this.wave, jitter, this.paths.length);
  }

  private spawnFromSchedule(entry: ScheduledSpawn): void {
    const archetype = this.enemyTable[entry.enemy];
    if (!archetype) return; // A missing id is a content bug; spawning nothing beats guessing.

    // Each enemy rolls its own lane (playtest request), so a two-lane day cannot be
    // answered by fortifying one side and ignoring the other. Off the gameplay stream, so
    // the roll is part of the replay like every other draw.
    const lane =
      entry.pathIndex === ROLL_LANE ? this.rng.nextInt(0, this.paths.length) : entry.pathIndex;
    const path = pathForGroup(this.paths, undefined, lane);
    const enemy = this.initEnemy(archetype, entry.hpMultiplier, path ? path.id : 0, 0, 0);
    if (path) resolvePosition(path, 0, enemy);
    if (enemy.flying && this.paths.length > 0) enemy.pathT = 0;
  }

  /** R-06-13 — children inherit the parent's progress, fanned out so they do not stack. */
  private spawnChild(archetype: EnemyArchetype, parent: Enemy, pathT: number, depth: number): void {
    if (this.enemies.countAlive() >= MAX_LIVE_ENEMIES) return;

    const parentArchetype = this.enemyTable[parent.archetype];
    // The parent's SCALE, not its health. `initEnemy` multiplies by the child's own base
    // hp, so dividing the parent's max hp by the CHILD's base — which is what this did —
    // cancels the child's stats out entirely and hands every child the parent's full
    // health pool. The Hive is the case that made it visible: a 9,099 hp boss split into
    // six sprinters of 9,099 each, a second phase six times the size of the first, which
    // three separate playtest reports described as the children being harder to kill than
    // the boss. They were right, and by a factor of six.
    //
    // Dividing by the PARENT's base recovers what the multiplier has always meant
    // everywhere else it is used (wave scale x jitter x group emphasis), so a child is
    // its own archetype scaled the same way its parent was.
    let hpMultiplier = 1;
    if (parentArchetype !== undefined) {
      const fraction = parentArchetype.splitHpFraction;
      hpMultiplier =
        fraction !== undefined && archetype.hp > 0
          ? (parent.maxHp * fraction) / (archetype.hp * (parentArchetype.splitInto?.count ?? 1))
          : parent.maxHp / parentArchetype.hp;
    }

    const child = this.initEnemy(archetype, hpMultiplier, parent.pathId, pathT, depth);
    const path = this.paths[parent.pathId];
    if (path) resolvePosition(path, child.pathT, child);
    else {
      child.x = parent.x;
      child.y = parent.y;
    }
  }

  private initEnemy(
    archetype: EnemyArchetype,
    hpMultiplier: number,
    pathId: number,
    pathT: number,
    splitDepth: number,
  ): Enemy {
    const e = this.enemies.spawn(this.allocateId());
    e.archetype = archetype.id;
    e.hp = archetype.hp * hpMultiplier;
    e.maxHp = e.hp;
    e.armor = archetype.armor;
    e.baseArmor = e.armor;
    e.speed = archetype.speed;
    e.baseSpeed = archetype.speed;
    e.bounty = archetype.bounty;
    e.leakDamage = archetype.leakDamage;
    e.size = archetype.size;
    e.flying = hasFlag(archetype, 'flying');
    e.shieldPerHit = archetype.shieldPerHit ?? 0;
    e.maxShieldHp = archetype.shieldHp ?? 0;
    e.shieldHp = e.maxShieldHp;
    e.shieldDownTicks = archetype.shieldDownTicks ?? 0;
    e.shieldDownUntilTick = 0;
    e.leapEveryTicks = archetype.leapEveryTicks ?? 0;
    e.leapTiles = archetype.leapTiles ?? 0;
    const immunities = archetype.statusImmunities ?? EMPTY_IMMUNITIES;
    e.immuneStun = immunities.includes('stun');
    e.immuneFreeze = immunities.includes('freeze');
    e.emergeUntilTick = this.tick + (archetype.emergeTicks ?? 0);
    e.teleportEveryTicks = archetype.teleportEveryTicks ?? 0;
    e.frontalCos = archetype.frontalCos ?? 0;
    e.frontalReduction = archetype.frontalReduction ?? 0;
    e.disableRange = archetype.disableRange ?? 0;
    e.disableRadius = archetype.disableRadius ?? 0;
    e.disableTicks = archetype.disableTicks ?? 0;
    e.disableEveryTicks = archetype.disableEveryTicks ?? 0;
    // Its first shot waits a full cycle, so it cannot open by deleting a board the moment
    // it walks on screen.
    e.disableReadyTick = this.tick + (archetype.disableEveryTicks ?? 0);
    e.splitDepth = splitDepth;
    e.pathId = pathId;
    e.pathT = pathT > 0 ? pathT : 0;
    e.spawnTick = this.tick;
    return e;
  }

  /**
   * A buff tile's non-class effects, in the effect union the synergy engine already
   * evaluates — so a tile needs no new evaluation code, only new data (R-08-22b's test).
   */
  private tileEffects(key: number): readonly SynergyEffect[] | undefined {
    const tile = this.buffTiles.get(key);
    if (!tile) return undefined;
    const effects: SynergyEffect[] = [];
    if (tile.rangeBonus !== undefined) {
      effects.push({
        k: 'stat',
        target: { s: 'sameClass' },
        stat: 'rangeBonus',
        op: 'add',
        value: tile.rangeBonus,
      });
    }
    if (tile.onHitStatus !== undefined) {
      effects.push({
        k: 'applyStatus',
        on: 'hit',
        status: tile.onHitStatus.status,
        magnitude: tile.onHitStatus.magnitude,
        durationTicks: tile.onHitStatus.durationTicks,
      });
    }
    return effects.length > 0 ? effects : undefined;
  }

  /** Is a tower standing on a tile that mends the Core? */
  private occupiedCoreHpTile(): BuffTileSpec | undefined {
    const towers = this.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i] as Tower;
      if (!t.alive) continue;
      const tile = this.buffTiles.get(t.tileY * 1000 + t.tileX);
      if (tile?.coreHpEveryNWaves !== undefined) return tile;
    }
    return undefined;
  }

  /**
   * The wave a surge pressed RIGHT NOW would apply to.
   *
   * In combat that is the wave being fought; in the build phase it is the one about to
   * start, which is what lets a surge be armed ahead of a round the player already knows
   * is the hard one.
   */
  private surgeTargetWave(): number {
    return this.phase === 'build' ? this.wave + 1 : this.wave;
  }

  private resetTowerCooldowns(): void {
    const items = this.towers.items;
    for (let i = 0; i < items.length; i++) {
      const t = items[i] as Tower;
      if (t.alive) t.cooldown = 0;
    }
  }

  /**
   * 2. TRD-08 — rebuild every mod on the board from scratch.
   *
   * Deliberately a no-op on clean ticks (R-08-04): the placed-tower set changes only on
   * place / move / sell / merge, so this full pass runs a handful of times per wave rather
   * than 60 times a second.
   */
  private sysSynergyRecompute(): void {
    if (this.synergyDirty) {
      this.synergyDirty = false;
      this.synergy = recomputeSynergies({
        towers: this.towers,
        towerTable: this.towerTable,
        classTable: this.config.classTable,
        runMods: this.runMods,
        thresholdShift: this.thresholdShift,
        runEffects: this.config.runEffects,
        classOnTile: (x, y) => this.buffTiles.get(y * 1000 + x)?.grantsClass,
        tileEffectsFor: (x, y) => this.tileEffects(y * 1000 + x),
        wave: this.wave,
        inCombat: this.phase === 'combat',
      });
      // bonusCoreHp GRANTS the hearts it adds, not just headroom (playtest report:
      // Warden's "+2 Core HP" raised the max and gave nothing — a max-only bonus is
      // literally invisible unless you were somehow going to overheal). The grant is
      // high-watered so re-crossing a threshold cannot be pumped: sell the sixth
      // Warden, re-place it, and no new hearts appear — only a bonus the run has
      // never reached before pays out. Losing the bonus only clamps HP that sits
      // above the new max; damage already taken is never refunded or re-inflicted.
      const bonus = this.runMods.bonusCoreHp;
      if (bonus > this.bonusCoreHpGranted) {
        this.coreHp += bonus - this.bonusCoreHpGranted;
        this.bonusCoreHpGranted = bonus;
      }
      this.coreHp = Math.min(this.coreHp, this.coreMaxHp + bonus);
    }

    this.tickSynergyFields();
  }

  /**
   * Field-wide synergy behaviour: Warden's slow aura and Frost-6's periodic freeze.
   *
   * Both are keyed off ABSOLUTE tick numbers rather than a countdown started when the
   * synergy activated (R-08-12), so the same seed fires them on the same ticks regardless
   * of when during the run the sixth Frost tower was placed.
   */
  /**
   * Status tuning as retuned by synergies — Venom-4 raises poison's cap and halves its
   * cadence (R-08 §4). Falls back to the defaults when no class has changed them.
   */
  statusTuning(): StatusTuning {
    const poison = this.synergy.statusTuning.poison;
    const chill = this.synergy.statusTuning.chill;
    if (!poison && !chill) return DEFAULT_STATUS_TUNING;
    return {
      poisonMaxStacks: poison?.cap ?? DEFAULT_STATUS_TUNING.poisonMaxStacks,
      poisonTickInterval: poison?.tickIntervalTicks ?? DEFAULT_STATUS_TUNING.poisonTickInterval,
      chillDurationMultiplier:
        chill?.durationMultiplier ?? DEFAULT_STATUS_TUNING.chillDurationMultiplier,
    };
  }

  private tickSynergyFields(): void {
    if (this.phase !== 'combat') return;

    for (const aura of this.synergy.auraStatuses) {
      for (const towerId of aura.towers) {
        const tower = this.towers.get(towerId);
        if (!tower) continue;
        this.grid.query(tower.x, tower.y, aura.radiusTiles, this.enemies, this.scratchIds);
        for (const enemyId of this.scratchIds) {
          const enemy = this.enemies.get(enemyId);
          if (enemy) {
            applyStatus(
              enemy,
              {
                status: aura.status,
                magnitude: aura.magnitude,
                durationTicks: aura.durationTicks,
                stacks: 1,
              },
              this.tick,
              this.statusTuning(),
            );
          }
        }
      }
    }

    for (const periodic of this.synergy.periodics) {
      if (this.tick % periodic.everyTicks !== 0) continue;
      for (const effect of periodic.then) {
        if (effect.k !== 'fieldStatus') continue;
        const enemies = this.enemies.items;
        for (let i = 0; i < enemies.length; i++) {
          const enemy = enemies[i] as Enemy;
          if (!enemy.alive) continue;
          applyStatus(
            enemy,
            {
              status: effect.status,
              magnitude: effect.magnitude,
              durationTicks: effect.durationTicks,
              stacks: 1,
            },
            this.tick,
            this.statusTuning(),
          );
        }
        const source = this.towers.get(this.synergy.statuses[0]?.contributingTowers[0] ?? -1);
        this.events.push({
          t: 'synergyProc',
          classId: periodic.classId,
          tier: 6,
          x: source?.x ?? this.core.x,
          y: source?.y ?? this.core.y,
        });
      }
    }
  }

  /**
   * What would change if this tower were placed (R-08-15).
   *
   * Drives the shop card's "+1 Frost → 4 ✦ unlocks …" hint, which is the single most
   * important piece of UI in the game — with every tower sharing one silhouette, it is how
   * a player learns the synergy web at all.
   *
   * Counts only; it does NOT mutate the world, and a test asserts the hash is unchanged
   * across a call.
   */
  previewPlacement(defId: string): SynergyDelta[] {
    const spec = this.towerTable[defId];
    if (!spec) return [];

    const { counts } = countClasses(this.towers, this.towerTable);
    const deltas: SynergyDelta[] = [];

    for (const classId of spec.classes) {
      const before = counts.get(classId) ?? 0;
      const after = before + 1;
      const beforeTier = activeTierFor(before, this.thresholdShift);
      const afterTier = activeTierFor(after, this.thresholdShift);
      const unlocked =
        afterTier > beforeTier
          ? (this.config.classTable[classId]?.tiers.find((t) => t.threshold === afterTier)
              ?.summary ?? null)
          : null;

      deltas.push({ classId, before, after, beforeTier, afterTier, unlocked });
    }

    return deltas;
  }

  /**
   * The inspector's attribution lines (TRD-09 R-09-21): every stat/aura write that
   * landed on this tower at the last recompute, with who granted it. Recorded by the
   * synergy engine at the accumulator write itself, so this can never disagree with
   * the mods the combat systems read.
   */
  statBreakdownFor(
    towerId: EntityId,
  ): readonly { stat: string; op: 'add' | 'mul'; value: number; source: string }[] {
    return this.synergy.statSources.get(towerId) ?? [];
  }

  /**
   * Would buying this tower complete a ★1 merge (TRD-09 R-09-09)?
   *
   * A purchase always lands as a ★1 bench copy and the merge check runs immediately
   * after the buy (TRD-07 R-07-09), so the question is exactly: do MERGE_COUNT − 1
   * other ★1 copies already exist across bench and board? Owned by the sim because
   * MERGE_COUNT is balance data the UI must not hardcode.
   */
  /** Merge the next wave's groups per enemy, keeping first-appearance order (B-4). */
  private upcomingWavePreview(): { defId: string; count: number; boss: boolean }[] {
    const spec = this.config.waves.find((w) => w.wave === this.wave + 1);
    if (!spec) return [];
    const order: string[] = [];
    const counts = new Map<string, number>();
    for (const group of spec.groups) {
      if (!counts.has(group.enemy)) order.push(group.enemy);
      counts.set(group.enemy, (counts.get(group.enemy) ?? 0) + group.count);
    }
    return order.map((defId) => ({
      defId,
      count: counts.get(defId) ?? 0,
      boss: (this.config.enemyTable[defId]?.flags ?? []).includes('boss'),
    }));
  }

  /** Copies of `defId` the player holds, split into the merge-eligible ★1s and the lot. */
  private ownedCopies(defId: string): { ownedStarOne: number; owned: number } {
    let ownedStarOne = 0;
    let owned = 0;
    for (const item of this.economy.bench) {
      if (!item || item.defId !== defId) continue;
      owned++;
      if (item.star === 1) ownedStarOne++;
    }
    const towers = this.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i] as Tower;
      if (!t.alive || t.defId !== defId) continue;
      owned++;
      if (t.star === 1) ownedStarOne++;
    }
    return { ownedStarOne, owned };
  }

  private buyWouldMerge(defId: string): boolean {
    let copies = 0;
    for (const item of this.economy.bench) {
      if (item && item.defId === defId && item.star === 1) copies++;
    }
    const towers = this.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i] as Tower;
      if (t.alive && t.defId === defId && t.star === 1) copies++;
    }
    return copies >= this.balance.MERGE_COUNT - 1;
  }

  /**
   * 3. TRD-06 §3 — consume the schedule built at wave start.
   *
   * The schedule is a flat sorted array and a cursor, not a per-tick search: spawns for
   * this tick are however many entries at the cursor carry `tick <= this.waveTick`. The
   * `<=` rather than `===` matters when the live enemy cap stalls a spawn (R-06-24) —
   * the backlog drains as slots free rather than being silently dropped.
   */
  private sysWaveDirector(): void {
    if (this.phase !== 'combat') return;

    while (this.spawnCursor < this.schedule.length) {
      const next = this.schedule[this.spawnCursor] as ScheduledSpawn;
      if (next.tick > this.waveTick) break;
      if (this.enemies.countAlive() >= MAX_LIVE_ENEMIES) break;
      this.spawnFromSchedule(next);
      this.spawnCursor++;
    }
    this.waveTick++;
  }

  /**
   * 4. TRD-05 §7 — expiries and the poison tick.
   *
   * Runs before movement so effective speed is read after this tick's chills have
   * expired, not before (see SYSTEM_ORDER). Poison is dealt through the same damage
   * pipeline as everything else so it cannot bypass the one-death guard.
   */
  private sysStatusTick(): void {
    const items = this.enemies.items;
    for (let i = 0; i < items.length; i++) {
      const e = items[i] as Enemy;
      if (!e.alive) continue;
      const poison = tickStatuses(e, this.tick);
      if (poison > 0) {
        // Credited, not attributed: `credit` moves the number onto the Venom tower's
        // damage counter without touching `lastHitBy`, so whose on-kill hooks fire and
        // who collects the bounty are both exactly as they were.
        const source = this.towers.get(e.statuses.poison.source);
        applyDamage(
          e,
          { amount: poison, kind: 'true', crit: false, credit: source },
          this.tick,
          this.events,
        );
      }
    }
  }

  /**
   * 5. TRD-06 §2 — advance `pathT`, then derive x/y from it.
   *
   * Never the other way round (R-06-03). Integrating x/y directly accumulates float error
   * over a 40-tile path across 25 waves, which is precisely the divergence that produces
   * two different scores from one daily seed.
   */
  private sysEnemyMovement(): void {
    if (this.phase !== 'combat') return;

    const items = this.enemies.items;
    for (let i = 0; i < items.length; i++) {
      const e = items[i] as Enemy;
      if (!e.alive) continue;

      // Rising out of the ground — it is on the board, it just is not here yet.
      if (this.tick < e.emergeUntilTick) {
        if (this.tick === e.spawnTick) {
          this.events.push({
            t: 'enemyEmerging',
            enemy: e.id,
            x: e.x,
            y: e.y,
            untilTick: e.emergeUntilTick,
          });
        }
        continue;
      }

      // Shield down: it is not walking, and it is not blinking either.
      //
      // The break window is the only time an enemy's health is reachable at all, so a
      // blink out of the board's reach mid-window would hand the reward straight back.
      // Suppressing movement here — above the teleport — is what makes "frozen" mean
      // frozen rather than "stationary but still cheating".
      if (e.maxShieldHp > 0 && e.shieldDownUntilTick > 0) {
        if (this.tick < e.shieldDownUntilTick) continue;
        e.shieldHp = e.maxShieldHp;
        e.shieldDownUntilTick = 0;
        this.events.push({ t: 'enemyShieldRestored', enemy: e.id, x: e.x, y: e.y });
      }

      e.speed = effectiveSpeed(e, this.tick);
      if (e.speed <= 0) continue;

      // Facing is read off the movement this tick actually makes, below. Captured here
      // so a leap or a blink — which move the enemy without a heading of their own —
      // cannot leave it facing backwards.
      const wasX = e.x;
      const wasY = e.y;

      // Prism cheats (playtest request): every N ticks of its own age it blinks across
      // its own approach, so the lane you fortified is not the lane it arrives in. The
      // coin-flip comes off the seeded stream, which is what makes a given day's Prism
      // cheat the same way for everyone who plays it.
      //
      // Mirrored about the CORE's row rather than the map's. Prism flies, so it beelines
      // the Core and the blink persists — and reflecting about the map centre could halve
      // its remaining distance every four seconds. That, not its speed, is what "the
      // final boss moves too fast" actually was: it was not walking faster, it was
      // repeatedly skipping most of the trip. Reflecting about `core.y` leaves |Δy| and
      // therefore the distance to the Core exactly unchanged — the lane flips, the clock
      // does not move.
      //
      // Only meaningful for a flyer. A path-follower's position is recomputed from
      // `pathT` a few lines below, so for one of those this is a no-op by construction.
      if (e.teleportEveryTicks > 0 && e.flying) {
        const age = this.tick - e.spawnTick;
        if (age > 0 && age % e.teleportEveryTicks === 0) {
          const rows = this.config.buildMask.length;
          const mirrored = 2 * this.core.y - e.y;
          // A blink that would land off the board is skipped rather than clamped —
          // clamping is exactly the thing that would shorten the trip again.
          if (rows > 0 && mirrored >= 0 && mirrored <= rows && this.rng.chance(0.75)) {
            const x0 = e.x;
            const y0 = e.y;
            e.y = mirrored;
            this.events.push({ t: 'enemyTeleported', enemy: e.id, x0, y0, x1: e.x, y1: e.y });
          }
        }
      }

      if (e.flying) {
        // Flyers ignore the path and make straight for the Core, but still keep `pathT`
        // as a monotonic progress proxy so `first`/`last` targeting stays uniform and
        // the comparators stay branch-free (R-06-05).
        const angle = angleTo(e.x, e.y, this.core.x, this.core.y);
        e.x += cos(angle) * e.speed * DT;
        e.y += sin(angle) * e.speed * DT;
        const path = this.paths[e.pathId];
        const total = path ? path.totalLength : 0;
        e.pathT = total - Math.sqrt(distSq(e.x, e.y, this.core.x, this.core.y));
        faceAlong(e, wasX, wasY);
        continue;
      }

      const path = this.paths[e.pathId];
      if (!path) continue;
      e.pathT += e.speed * DT;

      // Leapers (B-16): every N ticks of the enemy's OWN age it jumps a stretch of path.
      // Keyed off age rather than the absolute tick so two leapers hop out of phase, and
      // sitting after the speed<=0 early-out above so a stun or freeze grounds the hop —
      // that is the counterplay.
      if (e.leapEveryTicks > 0) {
        const age = this.tick - e.spawnTick;
        if (age > 0 && age % e.leapEveryTicks === 0) {
          const x0 = e.x;
          const y0 = e.y;
          e.pathT += e.leapTiles;
          resolvePosition(path, e.pathT, e);
          this.events.push({ t: 'enemyLeaped', enemy: e.id, x0, y0, x1: e.x, y1: e.y });
          faceAlong(e, wasX, wasY);
          continue;
        }
      }

      resolvePosition(path, e.pathT, e);
      faceAlong(e, wasX, wasY);
    }
  }

  /**
   * 6. TRD-05 §3 — rebuild the spatial index, then acquire targets.
   *
   * The rebuild lives here rather than in its own system because the index must be
   * current for targeting AND for every range query made later in the tick, and adding a
   * system to SYSTEM_ORDER would be a versioned change (TRD-01 R-01-11).
   */
  private sysTowerTargeting(): void {
    this.grid.rebuild(this.enemies);
    if (this.phase !== 'combat') return;

    const items = this.towers.items;
    for (let i = 0; i < items.length; i++) {
      const t = items[i] as Tower;
      if (!t.alive) continue;
      // Support towers (B-15) never fire — `attackInterval: 0` is the discriminator —
      // so acquiring a target for one is a range query spent on nothing every tick.
      if (t.attackInterval <= 0) continue;
      t.targetId = acquireTarget(t, this.enemies, this.grid, this.scratchIds, this.blocksSight);
    }
  }

  /**
   * 5b. Disablers (playtest request) — the only thing in the game that attacks a TOWER.
   *
   * It aims at the strongest tower in reach and the blast takes whatever is standing near
   * it, which is the whole point: a clumped board loses its cluster to one shot and a
   * spread board loses one tower. The lesson arrives from an enemy rather than from a
   * placement bonus, and it is aimed squarely at the board that stacks everything by the
   * spawn.
   */
  private sysEnemyAbilities(): void {
    if (this.phase !== 'combat') return;

    const enemies = this.enemies.items;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i] as Enemy;
      if (!e.alive || e.disableEveryTicks <= 0) continue;
      if (this.tick < e.emergeUntilTick) continue;
      if (this.tick < e.disableReadyTick) continue;

      const target = this.strongestTowerNear(e);
      if (!target) continue;

      e.disableReadyTick = this.tick + e.disableEveryTicks;
      const radiusSq = e.disableRadius * e.disableRadius;
      const caught: EntityId[] = [];
      const towers = this.towers.items;
      for (let j = 0; j < towers.length; j++) {
        const t = towers[j] as Tower;
        if (!t.alive) continue;
        // Immunity is checked per TOWER, not per shot: one tower recovering must not
        // shield the rest of the blast, and a tower that just came back must not be
        // knocked straight down again — see TOWER_DISABLE_RECOVERY_TICKS.
        if (this.tick < t.disableImmuneUntilTick) continue;
        if (distSq(t.x, t.y, target.x, target.y) > radiusSq) continue;
        t.disabledUntilTick = this.tick + e.disableTicks;
        t.disableImmuneUntilTick = t.disabledUntilTick + TOWER_DISABLE_RECOVERY_TICKS;
        caught.push(t.id);
      }

      // Emitted even when the blast caught nothing — the shot still happened, and a line
      // drawn with no result is exactly how a player learns the immunity window exists.
      this.events.push({
        t: 'towersDisabled',
        enemy: e.id,
        x0: e.x,
        y0: e.y,
        x: target.x,
        y: target.y,
        radius: e.disableRadius,
        untilTick: this.tick + e.disableTicks,
        towers: caught,
      });
    }
  }

  /**
   * The tower a Disabler aims at: most damage dealt this run, within its reach.
   *
   * Damage rather than distance, because the strongest tower is the one a clumped board
   * builds around — aiming there is what makes the blast land on the cluster. Ties break
   * on the lower entity id so the choice cannot depend on pool ordering.
   */
  private strongestTowerNear(e: Enemy): Tower | undefined {
    const rangeSq = e.disableRange * e.disableRange;
    let best: Tower | undefined;
    const towers = this.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i] as Tower;
      if (!t.alive) continue;
      if (distSq(t.x, t.y, e.x, e.y) > rangeSq) continue;
      if (!best || t.damageDealt > best.damageDealt) best = t;
    }
    return best;
  }

  /** 7. TRD-05 §4 — cooldowns and firing. Towers are idle during `build` (R-05-12). */
  private sysTowerFiring(): void {
    if (this.phase !== 'combat') return;

    const ctx = this.fireContext();
    const items = this.towers.items;
    for (let i = 0; i < items.length; i++) {
      const t = items[i] as Tower;
      if (!t.alive) continue;

      // Knocked out. The cooldown is deliberately NOT ticked while dark, so a tower comes
      // back on the cooldown it was holding rather than with a free instant volley.
      if (t.disabledUntilTick > this.tick) continue;

      if (t.cooldown > 0) t.cooldown--;
      if (t.targetId === NO_TARGET) {
        // A laser whose target left range loses its ramp immediately, not on its next
        // damage tick — otherwise sweeping between two enemies would keep the bonus
        // (TRD-05 R-05-32).
        t.beamStacks = 0;
        t.beamTargetId = NO_TARGET;
        continue;
      }
      if (t.cooldown > 0) continue;
      if (t.attackInterval <= 0) continue;

      fireTower(t, ctx);
      t.cooldown = advanceFireSchedule(t);
    }
  }

  /** 8. TRD-05 §5 — travelling projectiles, then live zones (B-18). */
  private sysProjectileMovement(): void {
    const ctx = this.fireContext();
    stepProjectiles(ctx, this.towers);
    // Zones only advance in combat: they are cleared on wave completion, and stepping
    // them during a build phase would burn their windows against an empty path.
    if (this.phase === 'combat') {
      stepZones({
        tick: this.tick,
        zones: this.zones,
        enemies: this.enemies,
        towers: this.towers,
        grid: this.grid,
        events: this.events,
        scratch: this.scratchIds,
        hooks: ctx.hooks,
      });
    }
  }

  /**
   * Rebuilt per system rather than cached, because `tick` and `events` change under it.
   * The scratch array it carries is shared and reused, which is where the allocation
   * saving that matters actually lives.
   */
  /**
   * Swarm's drones (TRD-08 §4, TRD-06 §5).
   *
   * Cadence is keyed off the absolute tick, not a per-tower countdown, so the same seed
   * summons on the same ticks however late in the run the sixth Swarm tower arrived
   * (R-08-12). The `summons` map holds at most one entry per tower because `summon` has
   * replace-not-stack semantics — tier 4 upgrades tier 2's drone rather than running a
   * second emitter beside it.
   */
  private spawnSynergyDrones(): void {
    if (this.phase !== 'combat' || this.synergy.summons.size === 0) return;

    for (const [towerId, summon] of this.synergy.summons) {
      if (this.tick % summon.everyTicks !== 0) continue;
      // Hard cap (R-06-19). Skipped, never queued: a backlog would dump 80 drones the
      // instant one expired.
      if (!canSummon(this.drones)) break;

      const tower = this.towers.get(towerId);
      if (!tower) continue;

      const drone = this.drones.spawn(this.allocateId());
      drone.ownerTower = tower.id;
      drone.x = tower.x;
      drone.y = tower.y;
      // Star-scaled, like every other damage the tower deals (B-17): a ★3 broodmother
      // whose brood still hit at ★1 numbers would make merging one feel like a nerf.
      const scaling = tower.starScaling[tower.star - 1] ?? 1;
      drone.damage = summon.flatDamage + tower.damage * scaling * summon.fromOwnerPercent;
      drone.expiresAtTick = this.tick + summon.lifetimeTicks;
      drone.targetId = NO_TARGET;
    }
  }

  /**
   * The context synergy hooks fire in (TRD-08 §5).
   *
   * Rebuilt once per tick rather than per hit — `awardGold` closes over `this`, and
   * allocating a closure inside the damage loop would be per-projectile garbage in the
   * hottest part of the frame.
   */
  private hookContext(): HookContext {
    return {
      tick: this.tick,
      events: this.events,
      enemies: this.enemies,
      towers: this.towers,
      grid: this.grid,
      scratch: this.scratchIds,
      rng: this.rng,
      tuning: this.statusTuning(),
      awardGold: this.awardGoldBound,
      chainDepth: 0,
    };
  }

  private fireContext(): FireContext {
    return {
      tick: this.tick,
      rng: this.rng,
      enemies: this.enemies,
      projectiles: this.projectiles,
      zones: this.zones,
      grid: this.grid,
      events: this.events,
      allocateId: this.allocateIdBound,
      scratch: this.scratchIds,
      hooks: this.hookContext(),
    };
  }

  /** 9. TRD-06 §5 — drones seek, strike and expire. */
  private sysSummonTick(): void {
    this.spawnSynergyDrones();
    tickMenders(this.enemies, this.enemyTable, this.grid, this.tick, this.scratchIds);
    stepDrones({
      tick: this.tick,
      drones: this.drones,
      enemies: this.enemies,
      towers: this.towers,
      grid: this.grid,
      events: this.events,
      scratch: this.scratchIds,
      expiry: this.synergy.summonExpiry,
    });
  }

  /**
   * 10. TRD-06 §4 — bounty, kill events, splitting.
   *
   * Every tombstoned enemy visible here died on this tick: the previous tick's dead were
   * swept by compaction at step 13. That is what lets this be a plain scan with no
   * "already resolved" bookkeeping, and why a bounty cannot be awarded twice.
   */
  private sysDeathResolution(): void {
    resolveDeaths({
      tick: this.tick,
      enemies: this.enemies,
      grid: this.grid,
      table: this.enemyTable,
      events: this.events,
      scratch: this.scratchIds,
      allocateId: this.allocateIdBound,
      spawnChild: this.spawnChildBound,
      awardBounty: this.awardBountyBound,
    });

    this.runKillHooks();
  }

  /**
   * On-kill synergy hooks — Arcanist's detonation, Venom's burst, Alchemist's gold drop.
   *
   * Runs here rather than at the killing blow because an explosion damages other enemies,
   * and doing that inside `projectileMovement` would mutate the array that system is still
   * walking (R-05-20).
   *
   * The victim list is snapshotted first: a detonation can kill more enemies, and those
   * deaths belong to the NEXT tick's resolution rather than extending this loop
   * indefinitely — which is the second half of the cascade guard, alongside chainDepth.
   */
  private runKillHooks(): void {
    const items = this.enemies.items;
    let pending: Enemy[] | null = null;

    for (let i = 0; i < items.length; i++) {
      const e = items[i] as Enemy;
      if (e.alive || e.lastHitBy === NO_TARGET) continue;
      (pending ??= []).push(e);
    }
    if (!pending) return;

    const hooks = this.hookContext();
    for (const victim of pending) {
      const tower = this.towers.get(victim.lastHitBy);
      // The tower may have been sold between the shot landing and this system running.
      if (tower) applyOnKillHooks(hooks, tower, victim);

      // Alchemist-6 is a RUN-level drop, not a per-tower hook: it should pay out no
      // matter which tower landed the blow.
      const drop = this.synergy.goldOnKill;
      if (drop && this.rng.chance(drop.chance)) this.earnGold(drop.amount, 'drop');

      victim.lastHitBy = NO_TARGET;
    }
  }

  /** 11. TRD-06 §6 — enemies that reached the Core. */
  private sysLeakResolution(): void {
    const items = this.enemies.items;
    for (let i = 0; i < items.length; i++) {
      const e = items[i] as Enemy;
      if (!e.alive) continue;

      if (e.flying) {
        if (distSq(e.x, e.y, this.core.x, this.core.y) >= LEAK_RADIUS_SQ) continue;
      } else {
        const path = this.paths[e.pathId];
        if (!path || e.pathT < path.totalLength) continue;
      }

      // Warden-6 flattens every leak to a fixed cost, so a boss walking in hurts exactly
      // as much as a runner does. 0 means the tier is not active.
      const override = this.runMods.leakDamageOverride;
      const damage = override > 0 ? override : e.leakDamage;
      this.coreHp -= damage;
      this.enemies.kill(e);
      // The Core's own position, not the leaker's: the hit is on the Core, and the
      // effect belongs where the player's eye goes when the heart count drops.
      this.events.push({
        t: 'coreDamaged',
        amount: damage,
        remaining: this.coreHp,
        x: this.core.x,
        y: this.core.y,
      });

      if (this.coreHp <= 0) {
        this.coreHp = 0;
        this.endRun(false);
        return;
      }
    }
  }

  /**
   * 12. Wave completion (TRD-06 R-06-12).
   *
   * Cleared when the schedule is exhausted AND nothing is left alive — never on a timer.
   * A timer would end a wave while a slowed straggler was still walking, which reads as
   * the game cheating.
   */
  private sysWaveCompletion(): void {
    if (this.phase !== 'combat') return;

    // A wave with nothing scheduled never completes on its own. Every authored wave has
    // at least one group of at least one enemy (TRD-02 WaveDefSchema), so this only
    // catches a world running without wave content — which the chassis tests step
    // deliberately, and which would otherwise race to `ended` on its first tick.
    if (this.schedule.length === 0) return;

    if (this.spawnCursor < this.schedule.length) return;
    if (this.enemies.countAlive() > 0) return;

    this.events.push({ t: 'waveCleared', wave: this.wave });
    // Zones do not carry across waves — their window was against THIS wave's enemies,
    // and a leftover telegraph pulsing through the build phase reads as a stuck effect.
    this.zones.clear();

    if (this.wave >= this.config.totalWaves) {
      this.endRun(true);
      return;
    }
    // R-07-24 §9 — the payout order is fixed, and interest is taken on gold held before
    // the wave's income lands (R-07-25).
    const payout = computePayout(this.economy.gold, this.wave, this.balance, this.runMods);
    this.earnGold(payout.total, 'waveClear');

    // A Core-mending tile pays out every Nth wave CLEARED while it is occupied, counted
    // on its own clock rather than the wave number — leave the tile and the count stops
    // where it was, so the payout tracks the commitment rather than the calendar.
    const mender = this.occupiedCoreHpTile();
    if (mender?.coreHpEveryNWaves !== undefined) {
      this.coreTileWaves++;
      if (this.coreTileWaves % mender.coreHpEveryNWaves === 0) {
        const ceiling = this.coreMaxHp + this.runMods.bonusCoreHp;
        if (this.coreHp < ceiling) {
          this.coreHp++;
          this.events.push({ t: 'coreMended', amount: 1, remaining: this.coreHp, x: this.core.x, y: this.core.y });
        }
      }
    }

    this.economy.rerollsThisRound = 0;
    // R-07-16 — unused placements do not bank. Banking removes the round's tension.
    this.economy.placementsLeft = this.economy.placementsPerRound + this.runMods.bonusPlacements;
    // R-07-26: a locked shop survives the wave clear — the good tower you could not
    // afford yet stays. Skipping the roll also skips its RNG draws, which is fine:
    // the lock state is part of the replayed command stream.
    if (!this.economy.shopLocked) {
      rollShop(this.economy, this.shopPool, this.balance, this.wave + 1, this.shopRng);
    }
    this.phase = 'build';
    this.synergyDirty = true;
  }

  /** R-06-22 / R-06-23 — the run stops here; nothing continues after it. */
  private endRun(victory: boolean): void {
    this.phase = 'ended';
    this.victory = victory;
    this.enemies.clear();
    this.projectiles.clear();
    this.drones.clear();
    this.zones.clear();
    this.events.push({
      t: 'runEnded',
      score: this.score,
      wavesCleared: this.wavesCleared,
      victory,
    });
  }

  /** 13. TRD-01 R-01-08 — owned here, not by a gameplay TRD. */
  private sysCompaction(): void {
    this.enemies.compact();
    this.towers.compact();
    this.projectiles.compact();
    this.drones.compact();
    this.zones.compact();
  }

  // ── Hashing ─────────────────────────────────────────────────────────────────────────

  /**
   * The witness a submitted score is validated against (TRD-11 §5 step 10).
   *
   * Covers the run's entire history, not just where it ended up — so shifting a single
   * command by one tick changes it even if the run finishes in the same state.
   */
  get runHash(): number {
    return this.rolling;
  }

  private foldTickIntoRunHash(): void {
    const h = new Hasher(this.rolling);
    h.int(this.tick);
    h.str(this.phase);
    h.int(this.wave);
    h.int(this.coreHp);
    h.bigint(this.rng.position);

    // Command TIMING is part of the witness, not just command effects.
    //
    // Without this, a command the sim rejects as a no-op — buying with a full bench,
    // selling a tower that does not exist — could be moved freely around the log without
    // changing the hash, because it leaves no trace in world state. Folding in the
    // running applied-count makes the arrival tick itself observable, so any reordering
    // of the log is caught even when the commands do nothing (TRD-11 R-11-09).
    h.int(this.commandsApplied);

    if (this.tick % FULL_HASH_INTERVAL === 0) {
      h.u32(this.hash());
    }

    this.rolling = h.value;
  }

  /**
   * Order-dependent FNV-1a over all observable state at THIS instant (R-01-13).
   *
   * Use for per-tick divergence hunting: when a determinism test fails you want the FIRST
   * divergent tick, because that is where the bug is. By the final tick two runs have
   * drifted beyond recognition.
   *
   * Do not submit this — submit `runHash`.
   */
  hash(): number {
    const h = new Hasher();
    h.int(this.tick);
    h.str(this.phase);
    h.int(this.wave);
    h.int(this.coreHp);
    h.int(this.economy.gold);
    h.int(this.economy.placementsLeft);
    h.int(this.economy.shopLocked ? 1 : 0);
    h.bigint(this.rng.position);
    // The shop stream is part of the witness: a replay handed the wrong `shopSeed`
    // fails here rather than several waves later as an unexplainable score mismatch.
    h.bigint(this.shopRng.position);

    // Insertion order is part of the contract (TRD-00 R-00-05), so iterate by index.
    const enemies = this.enemies.items;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i] as Enemy;
      if (!e.alive) continue;
      h.int(e.id).float(e.x).float(e.y).float(e.pathT).float(e.hp);
    }

    const towers = this.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i] as Tower;
      if (!t.alive) continue;
      h.int(t.id).int(t.tileX).int(t.tileY).int(t.star).int(t.cooldown).int(t.shotsFired);
      // Burst position and laser ramp are live firing state: two worlds that agree on
      // everything else but disagree on where a burst is will diverge on the next tick.
      h.int(t.burstRemaining).int(t.beamStacks).int(t.beamTargetId);
    }

    const projectiles = this.projectiles.items;
    for (let i = 0; i < projectiles.length; i++) {
      const p = projectiles[i] as Projectile;
      if (!p.alive) continue;
      h.int(p.id).float(p.x).float(p.y).float(p.damage);
    }

    const drones = this.drones.items;
    for (let i = 0; i < drones.length; i++) {
      const d = drones[i] as Drone;
      if (!d.alive) continue;
      h.int(d.id).float(d.x).float(d.y);
    }

    const zones = this.zones.items;
    for (let i = 0; i < zones.length; i++) {
      const z = zones[i] as Zone;
      if (!z.alive) continue;
      // Position AND clocks: two worlds that agree on where a zone sits but disagree on
      // where its strike cadence is will diverge on the very next strike tick.
      h.int(z.id).float(z.x).float(z.y).int(z.windupLeft).int(z.ticksLeft).int(z.sinceStrike);
    }

    return h.value;
  }

  /**
   * TRD-01 R-01-16 — always a structural copy, never live references into world state.
   *
   * React holding a live entity and mutating it during a render would be a determinism
   * failure that only ever shows up as a rejected leaderboard submission.
   */
  snapshotForUI(): UiSnapshot {
    let boss: UiSnapshot['boss'] = null;
    const enemyItems = this.enemies.items;
    for (let i = 0; i < enemyItems.length; i++) {
      const e = enemyItems[i] as Enemy;
      if (!e.alive || e.maxShieldHp <= 0) continue;
      boss = {
        archetype: e.archetype,
        hp: e.hp,
        maxHp: e.maxHp,
        shieldHp: e.shieldHp,
        maxShieldHp: e.maxShieldHp,
        downUntilTick: e.shieldDownUntilTick,
        emerging: this.tick < e.emergeUntilTick,
      };
      break;
    }
    const ctx = this.mergeContext();
    const placedTowers: UiSnapshot['placedTowers'] = [];
    const towers = this.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i] as Tower;
      if (!t.alive) continue;
      placedTowers.push({
        id: t.id,
        defId: t.defId,
        star: t.star,
        tileX: t.tileX,
        tileY: t.tileY,
        range: effectiveRange(t),
        damage: Math.floor(
          (t.damage * (t.starScaling[t.star - 1] ?? 1) + t.mods.flatDamage) *
            t.mods.damageMultiplier,
        ),
        baseDamage: Math.floor(t.damage * (t.starScaling[t.star - 1] ?? 1)),
        attackInterval: cooldownTicks(t) / TICK_RATE,
        critChance: t.mods.critChance,
        targetingMode: t.targetingMode,
        damageDealt: t.damageDealt,
        assistDealt: t.assistDealt,
        sellRefund: sellRefund(t.investedGold, this.balance),
        mergeReady: mergeReadyFor(ctx, t),
        support: this.towerTable[t.defId]?.support === true,
        surgeActive: this.phase === 'combat' && t.surgeWave === this.wave,
        surgeArmed: this.phase === 'build' && t.surgeWave === this.wave + 1,
        surgeReady:
          this.towerTable[t.defId]?.support === true &&
          this.phase !== 'ended' &&
          this.surgeTargetWave() >= t.surgeReadyWave &&
          t.surgeWave !== this.surgeTargetWave(),
        surgeBackInWaves: Math.max(0, t.surgeReadyWave - this.surgeTargetWave()),
      });
    }

    return {
      tick: this.tick,
      boss,
      phase: this.phase,
      wave: this.wave,
      totalWaves: this.config.totalWaves,
      coreHp: this.coreHp,
      // EFFECTIVE max — mutators and meta nodes shift it via bonusCoreHp, and "8/20"
      // on a Glass Cannon day reads as twelve lives already lost (backlog B-1).
      coreMaxHp: this.coreMaxHp + this.runMods.bonusCoreHp,
      gold: this.economy.gold,
      placementsLeft: this.economy.placementsLeft,
      rerollCost: 2 + this.economy.rerollsThisRound,
      shop: this.economy.shop.map((s) =>
        s
          ? {
              defId: s.defId,
              cost: s.cost,
              wouldMerge: this.buyWouldMerge(s.defId),
              mergesOnBench:
                this.benchStarOneMatches(s.defId).length >= this.balance.MERGE_COUNT - 1,
              ...this.ownedCopies(s.defId),
              deltas: this.previewPlacement(s.defId),
            }
          : null,
      ),
      bench: this.economy.bench.map((b) =>
        b
          ? {
              ...b,
              sellRefund: sellRefund(b.investedGold, this.balance),
              deltas: this.previewPlacement(b.defId),
            }
          : null,
      ),
      shopLocked: this.economy.shopLocked,
      nextWave: this.phase === 'build' ? this.upcomingWavePreview() : null,
      currentWaveIsBoss:
        this.phase === 'combat' &&
        (this.config.waves.find((w) => w.wave === this.wave)?.isBoss ?? false),
      nextWaveIsBoss: this.config.waves.find((w) => w.wave === this.wave + 1)?.isBoss ?? false,
      placedTowers,
      synergies: this.synergy.statuses.map((s) => ({
        ...s,
        contributingTowers: [...s.contributingTowers],
      })),
      enemiesRemaining: this.enemies.countAlive(),
      towersPlaced: this.placedTowerCount(),
      maxTowers: this.balance.MAX_PLACED_TOWERS,
    };
  }

  /** TRD-06 R-06-23. Cumulative income, not gold held. */
  get score(): number {
    return this.wavesCleared * 1000 + this.economy.goldEarnedTotal + this.coreHp * 100;
  }

  get wavesCleared(): number {
    if (this.phase === 'build' || this.phase === 'ended') return this.wave;
    return Math.max(0, this.wave - 1);
  }

  result(): RunResult {
    return {
      score: this.score,
      wavesCleared: this.wavesCleared,
      coreHp: this.coreHp,
      victory: this.victory,
      hash: this.runHash,
      finalTick: this.tick,
    };
  }
}

/**
 * Extra starting gold granted by mutators and meta-tree bundles.
 *
 * Summed rather than taking the last one, so two sources both pay out — the alternative
 * silently drops one and looks like the bug this function exists to fix.
 */
export function startingGoldBonus(runEffects: readonly RunEffect[] | undefined): number {
  let bonus = 0;
  for (const bundle of runEffects ?? []) {
    for (const effect of bundle.effects) {
      if (effect.k === 'startingGold') bonus += effect.amount;
    }
  }
  return bonus;
}

/**
 * Point an enemy along the movement it just made.
 *
 * A zero delta leaves the old heading alone, which is the whole reason this is a function
 * rather than an assignment: a stunned or frozen enemy must keep facing where it was
 * going, or hard CC would quietly strip a frontal plate off it.
 */
function faceAlong(e: Enemy, wasX: number, wasY: number): void {
  const dx = e.x - wasX;
  const dy = e.y - wasY;
  if (dx === 0 && dy === 0) return;
  e.facingX = dx;
  e.facingY = dy;
}

const EMPTY_IMMUNITIES: readonly StatusKind[] = [];

const EMPTY_COMMANDS: readonly Command[] = [];
const EMPTY_SCHEDULE: readonly ScheduledSpawn[] = [];

/** One placed tower at the end of a replayed run — what the leaderboard stores. */
export interface BoardTowerSummary {
  defId: TowerId;
  star: StarLevel;
  tileX: number;
  tileY: number;
  damageDealt: number;
  assistDealt: number;
}

export interface DetailedRunResult {
  result: RunResult;
  /** The final board with per-tower damage, in placement order. */
  board: BoardTowerSummary[];
}

/**
 * Headless replay (R-01-14) — the exact function the server calls to validate a submitted
 * score (TRD-11 §5). No renderer, no timing, as fast as the CPU allows.
 *
 * This existing at all is the justification for the whole sim/render split.
 */
export function replay(
  seed: bigint,
  config: RunConfig,
  log: ReplayLog,
): RunResult {
  return replayDetailed(seed, config, log).result;
}

/**
 * Replay plus the final board and its damage attribution.
 *
 * This is what makes leaderboard run-viewing trustworthy for free: the summary is read
 * off the SERVER'S OWN re-simulation, never taken from the client, so a stored board
 * can no more be faked than a stored score can.
 */
export function replayDetailed(
  seed: bigint,
  config: RunConfig,
  log: ReplayLog,
): DetailedRunResult {
  // The shop seed is part of the log, not the config: the config is the DAY (cached and
  // shared), the shop is the RUN.
  const world = new World(seed, config, log.shopSeed !== undefined ? { shopSeed: log.shopSeed } : {});

  // Group by tick so `step` receives exactly what it received live. Building this map
  // once beats filtering the log per tick, which would be O(ticks × commands).
  const byTick = new Map<number, Command[]>();
  for (const { tick, cmd } of log.commands) {
    const bucket = byTick.get(tick);
    if (bucket) bucket.push(cmd);
    else byTick.set(tick, [cmd]);
  }

  while (world.tick < log.finalTick && world.phase !== 'ended') {
    world.step(byTick.get(world.tick) ?? EMPTY_COMMANDS);
  }

  const board: BoardTowerSummary[] = [];
  for (const t of world.towers.items) {
    if (!t.alive) continue;
    board.push({
      defId: t.defId,
      star: t.star,
      tileX: t.tileX,
      tileY: t.tileY,
      damageDealt: t.damageDealt,
      assistDealt: t.assistDealt,
    });
  }

  return { result: world.result(), board };
}

export { SIM_VERSION };
